import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Navbar,
  Footer,
  FloatingActions,
  SocialRow,
  ContactForm,
} from "../components/Shared";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

// Service Modal Data
const LOGISTICS_SERVICES_DATA = {
  customs: {
    title: "Customs Clearance",
    icon: "fa-file-invoice",
    description: "Seamless clearance for sea & air cargo from Sharjah Port Khalid, Jebel Ali, and Dubai Airport.",
    details: [
      "Full documentation processing with CID, MOFA, Health, and Municipality approvals.",
      "Customs duty assessment & tariff classification to optimize import costs.",
      "Dedicated customs brokers operating 24/7 at major UAE entry ports.",
      "Swift clearance to prevent demurrage and port storage charges."
    ]
  },
  seafreight: {
    title: "Sea Freight",
    icon: "fa-ship",
    description: "FCL, LCL, Break Bulk and RORO with best rates from China, Pakistan, India to the UAE and worldwide.",
    details: [
      "Weekly departures linking major international hubs.",
      "Flexible options: Full Container Load (FCL) & Less than Container Load (LCL).",
      "Specialized handling for oversized, project, or roll-on/roll-off (RORO) cargo.",
      "End-to-end container tracking from port-of-origin to port-of-delivery."
    ]
  },
  airfreight: {
    title: "Air Freight",
    icon: "fa-plane",
    description: "DG and NDG cargo handling from UAE to Europe, USA, Canada, Australia, India, Pakistan and beyond.",
    details: [
      "Express, standard, and deferred air charter options.",
      "Certified handling for Dangerous Goods (DG) & Non-Dangerous Goods (NDG).",
      "Door-to-door, airport-to-airport, and port-to-door delivery networks.",
      "Strong alliances with premier global airlines for guaranteed cargo space."
    ]
  },
  landfreight: {
    title: "Land Freight",
    icon: "fa-truck",
    description: "Trailers (12m/15m), Flat Bed, Box, Curtain Side, Heavy Low beds & Pickups across UAE, Oman, KSA, Qatar, Bahrain, Kuwait & Jordan.",
    details: [
      "Cross-border land transportation across the GCC network.",
      "Diverse fleet: Flatbeds, curtain-siders, box trucks, and heavy low-bed options.",
      "Real-time GPS fleet tracking and daily transit status updates.",
      "Customs clearance assistance at all Middle Eastern border checkpoints."
    ]
  },
  express: {
    title: "Express Courier",
    icon: "fa-box-open",
    description: "Door-to-door fast & efficient export and import services worldwide with guaranteed transit times.",
    details: [
      "Time-sensitive parcel delivery with priority routing.",
      "Simplified custom procedures for fast-track import/export packages.",
      "Complete end-to-end tracking visibility with signature verification.",
      "Optimized solutions for e-commerce fulfillment and commercial samples."
    ]
  },
  warehousing: {
    title: "Warehousing & 3PL",
    icon: "fa-warehouse",
    description: "Cost-effective warehousing, repacking, distribution and integrated 3PL solutions for seamless flow of goods.",
    details: [
      "Secure storage facilities strategically positioned near UAE's key transport hubs.",
      "Value-added services: professional repacking, labeling, and sorting.",
      "Inventory control management with customized distribution frameworks.",
      "Seamless integration with our freight divisions for swift, end-to-end delivery."
    ]
  }
};

const services = [
  {
    id: "customs",
    icon: "fa-file-invoice",
    title: "Customs Clearance",
    desc: "Seamless clearance for sea & air cargo from Sharjah Port Khalid, Jebel Ali and Dubai Airport with CID, MOFA, Health & Municipality approvals."
  },
  {
    id: "seafreight",
    icon: "fa-ship",
    title: "Sea Freight",
    desc: "FCL, LCL, Break Bulk and RORO with best rates from China, Pakistan, India to the UAE and worldwide."
  },
  {
    id: "airfreight",
    icon: "fa-plane",
    title: "Air Freight",
    desc: "DG and NDG cargo handling from UAE to Europe, USA, Canada, Australia, India, Pakistan and beyond."
  },
  {
    id: "landfreight",
    icon: "fa-truck",
    title: "Land Freight",
    desc: "Trailers (12m/15m), Flat Bed, Box, Curtain Side, Heavy Low beds & Pickups across UAE, Oman, KSA, Qatar, Bahrain, Kuwait & Jordan."
  },
  {
    id: "express",
    icon: "fa-box-open",
    title: "Express Courier",
    desc: "Door-to-door fast & efficient export and import services worldwide with guaranteed transit times."
  },
  {
    id: "warehousing",
    icon: "fa-warehouse",
    title: "Warehousing & 3PL",
    desc: "Cost-effective warehousing, repacking, distribution and integrated 3PL solutions for seamless flow of goods."
  },
];

// =========================================================================
// MOBILE TEAM SECTION COMPONENT
// =========================================================================
function MobileTeamSection() {
  const teamMembers = [
    {
      name: "Zubair Bin Mohammed Bajabar",
      role: "Managing Director",
      image: "/team-member1.png",
      email: "#",
      linkedin: "https://www.linkedin.com/in/zubair-bajajabar-772b03aa",
    },
    {
      name: "Muhammad Ahsan",
      role: "Operation And Admin Head",
      image: "/team-member3.png",
      email: "#",
      linkedin: "https://www.linkedin.com/in/muhammad-ahsan-411a3a8b",
    },
    {
      name: "Maz Pasha",
      role: "Assistant Operations Manager",
      image: "/team-member4.png",
      email: "#",
      linkedin: "#",
    },
    {
      name: "Ghouse Chida",
      role: "Accounts Manager",
      image: "/team-member2.png",
      email: "#",
      linkedin: "#",
    },
  ];

  return (
    <section className="py-12 px-4">
      {/* Header */}
      <motion.div
        className="text-center mb-8 space-y-3"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
      >
        <motion.span
          variants={fadeUp}
          className="inline-flex items-center px-3.5 py-1 rounded-full bg-blue-50 text-blue-700 font-bold tracking-[0.2em] uppercase text-[11px] border border-blue-100"
        >
          Our Team
        </motion.span>

        <motion.h2 variants={fadeUp} className="text-2xl font-black text-slate-900">
          Meet Our Leadership
        </motion.h2>

        <motion.p variants={fadeUp} className="max-w-md mx-auto text-slate-500 text-xs leading-relaxed">
          Dedicated professionals delivering reliable logistics, customs
          clearance, freight forwarding and global trade solutions with
          operational excellence.
        </motion.p>
      </motion.div>

      {/* Team Grid */}
      <motion.div
        className="grid grid-cols-2 gap-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={stagger}
      >
        {teamMembers.map((member) => (
          <motion.div
            key={member.name}
            variants={fadeUp}
            className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm active:scale-[0.98] transition-transform duration-200 flex flex-col"
          >
            {/* Image */}
            <div className="relative h-32 sm:h-40 overflow-hidden bg-slate-100">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col px-3 py-3">
              <h3 className="text-center text-[12px] font-extrabold text-slate-900 leading-tight min-h-[32px] flex items-center justify-center">
                {member.name}
              </h3>

              <p className="mt-1 text-center text-slate-500 text-[10px] leading-tight min-h-[24px] flex items-center justify-center">
                {member.role}
              </p>

              {/* Footer */}
              <div className="mt-auto pt-2.5 border-t border-slate-100 flex items-center justify-center gap-2">
                <a
                  href={member.email}
                  className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center text-xs"
                  title="Email"
                >
                  <i className="fas fa-envelope"></i>
                </a>

                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-xs"
                  title="LinkedIn"
                >
                  <i className="fab fa-linkedin-in"></i>
                </a>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

// =========================================================================
// MOBILE CONTACT FORM COMPONENT (uses shared ContactForm — same as MobileLanding)
// =========================================================================
function MobileContactForm() {
  return (
    <div className="bg-[#F8FAFC] py-10 px-4 rounded-[32px] border border-slate-100 shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
      <div className="space-y-8">
        {/* Info Section */}
        <div className="space-y-4 text-center sm:text-left">
          <span className="inline-flex items-center px-3.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-wider text-[11px]">
            Contact Us
          </span>
          <h2 className="text-3xl font-black text-[#102A54] tracking-tight leading-tight">
            Send Your Inquiry
          </h2>
          <p className="text-slate-500 font-medium leading-relaxed text-sm">
            Reach out to our team for freight forwarding, customs clearance, land transport, warehousing, and global logistics solutions.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 text-[#0A56C6] font-bold text-sm">
              <i className="fas fa-user-tie text-xs" />
              <span>Zubair Bin Mohammed</span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5">Managing Director</p>
            <div className="mt-3 space-y-1 text-slate-600 text-xs font-semibold">
              <a href="mailto:zubair@bajabarinc.ae" className="block hover:text-[#0A56C6]">
                zubair@bajabarinc.ae
              </a>
              <a href="tel:+971525131571" className="block hover:text-[#0A56C6]">
                +971 525131571
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 text-[#0A56C6] font-bold text-sm">
              <i className="fas fa-user-tie text-xs" />
              <span>Mohammed Ahsan</span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5">Business Development</p>
            <div className="mt-3 space-y-1 text-slate-600 text-xs font-semibold">
              <a href="mailto:mohammed@bajabarinc.ae" className="block hover:text-[#0A56C6]">
                sales@bajabarinc.ae
              </a>
              <a href="tel:+9718125211886" className="block hover:text-[#0A56C6]">
                +971 81252 11886
              </a>
            </div>
          </div>
        </div>

        {/* Social Icons */}
        <div className="flex flex-wrap justify-center gap-2.5 pt-1">
          {["facebook-f", "instagram", "linkedin-in", "twitter", "whatsapp", "youtube"].map((soc) => (
            <a
              key={soc}
              href="#"
              className="w-9 h-9 rounded-full border border-slate-200 text-slate-500 bg-white hover:bg-[#0A56C6] hover:text-white flex items-center justify-center transition text-xs"
            >
              <i className={`fab fa-${soc}`} />
            </a>
          ))}
        </div>

        {/* Form Card — now delegates to the shared ContactForm component */}
        <div className="bg-white rounded-[28px] p-6 border border-slate-100 shadow-md">
          <h3 className="text-2xl font-black text-[#102A54]">Get In Touch</h3>
          <p className="text-slate-400 text-xs font-medium mt-1">
            Fill the form and our team will contact you shortly.
          </p>

          <div className="mt-6">
            <ContactForm
              division="logistics"
              services={[
                "Customs Clearance",
                "Sea Freight",
                "Air Freight",
                "Land Freight",
                "Express Courier",
                "Warehousing & 3PL",
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// MOBILE JCTRANS CERTIFICATION COMPONENT
// =========================================================================
function MobileCertification() {
  return (
    <section className="relative py-12 px-4 bg-gradient-to-br from-white via-blue-50/40 to-white border-t border-b border-blue-100/60 overflow-hidden">
      <div className="space-y-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-40 h-auto">
            <img 
              src="/updated-logo.png" 
              alt="JCTRANS Premium Member" 
              className="w-full h-auto object-contain mx-auto"
            />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 font-bold text-xs shadow-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <i className="fas fa-check-circle" /> JCTRANS Premium Member
          </div>
        </div>

        <h2 className="text-2xl font-black text-slate-950 leading-tight">
          Global Recognition & 
          <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
            Freight Logistics Trust
          </span>
        </h2>

        <p className="text-slate-600 text-xs leading-relaxed font-medium">
          Bajabar Incorporation FZE is proudly recognized as a JCTRANS Premium Member, demonstrating our commitment to trusted international freight forwarding and global business excellence.
        </p>

        <div className="flex flex-col items-center gap-2 text-slate-700 font-semibold text-xs">
          <div className="flex items-center gap-2">
            <i className="fas fa-id-card text-blue-700" />
            <span>Member ID: 161302</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="fas fa-calendar-alt text-blue-700" />
            <span>Valid Term: 2026.07 - 2027.06</span>
          </div>
        </div>

        {/* Certificate Card */}
        <a 
          href="/certificate.png" 
          target="_blank" 
          rel="noopener noreferrer"
          className="block relative bg-white border border-blue-100 rounded-2xl p-3 shadow-md mt-4"
        >
          <div className="relative overflow-hidden rounded-xl bg-slate-50 aspect-[4/3] border border-slate-100 flex items-center justify-center">
            <img 
              src="/certificate.png" 
              alt="Bajabar JCTRANS Membership Certificate" 
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <p className="text-slate-500 text-[10px] mt-2 flex items-center justify-center gap-1 font-semibold">
            <i className="fas fa-search-plus text-blue-600" /> Tap to view full certificate
          </p>
        </a>
      </div>
    </section>
  );
}

// =========================================================================
// MOBILE WHY US SECTION
// =========================================================================
function MobileWhyUs() {
  const features = [
    "Expert Customs Clearance",
    "Global Network Coverage",
    "Competitive Pricing",
    "Express Courier Services",
    "Specialised Stock Consolidation",
    "Dedicated Account Manager",
  ];

  return (
    <section className="py-14 px-4 bg-white" id="why">
      <div className="relative">
        {/* IMAGE */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative rounded-[28px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.12)]">
            <img
              src="/backgroundlanding.png"
              alt="Logistics"
              className="w-full h-[260px] sm:h-[320px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3A]/70 via-[#0B1B3A]/10 to-transparent" />

            <div className="absolute top-3 right-3 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2.5 max-w-[75%]">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white grid place-items-center text-sm shrink-0">
                <i className="fas fa-plane" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-slate-900 leading-tight">
                  Worldwide Logistics
                </h3>
                <p className="text-slate-500 text-[10px]">
                  Fast & Secure Delivery
                </p>
              </div>
            </div>

            <div className="absolute bottom-3 left-3 right-3 grid grid-cols-3 gap-2">
              {[
                ["15+", "YEARS"],
                ["500+", "CLIENTS"],
                ["100K+", "SHIPMENTS"],
              ].map(([num, label]) => (
                <div key={label} className="bg-white rounded-xl py-2.5 text-center shadow-lg">
                  <div className="text-base font-black text-slate-900">
                    {num}
                  </div>
                  <div className="text-[8px] tracking-[0.15em] text-slate-500 mt-1 font-semibold">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* TEXT */}
        <motion.div
          className="mt-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.span
            variants={fadeUp}
            className="inline-flex px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 font-bold tracking-[0.15em] uppercase text-[10px]"
          >
            Why Choose Us
          </motion.span>

          <motion.h2
            variants={fadeUp}
            className="mt-4 text-2xl sm:text-3xl font-black text-slate-900 leading-tight tracking-[-1px]"
          >
            Trusted Global Logistics
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-500">
              Solutions
            </span>
          </motion.h2>

          <motion.p variants={fadeUp} className="mt-4 text-slate-600 leading-[1.9] text-sm">
            We provide reliable freight forwarding, customs clearance and logistics solutions with strong operational excellence across UAE and global markets.
          </motion.p>

          <motion.div variants={stagger} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {features.map((f) => (
              <motion.div key={f} variants={fadeUp} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs shrink-0">
                  <i className="fas fa-check" />
                </div>
                <span className="text-slate-700 font-semibold text-sm">{f}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// =========================================================================
// MAIN MOBILE LOGISTICS COMPONENT
// =========================================================================
export default function MobileLogistics() {
  const [activeService, setActiveService] = useState(null);

  return (
    <div className="min-h-screen bg-white overflow-hidden antialiased">
      {/* FIXED NAVBAR */}
      <div className="fixed top-0 left-0 w-full z-[100]">
        <Navbar showProducts={false} showVertiv={false} showBrands={false} />
      </div>

      {/* HERO */}
      <section
        id="home"
        className="relative pt-24 pb-12 px-4 min-h-[60vh] flex flex-col justify-center bg-cover bg-center"
        style={{
          backgroundImage: "url('/backgroundlanding.png')",
        }}
      >
        <div className="absolute inset-0 bg-white/85 backdrop-blur-[1px]" />
        <div className="relative z-20 space-y-6 text-center">
          <h1 className="font-black leading-tight tracking-tight text-slate-950 text-3xl sm:text-4xl">
            Your Freight
            <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
              & Logistics
            </span>
            <span className="block text-slate-900">Solutions</span>
          </h1>

          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium max-w-xs mx-auto">
            Trusted freight forwarding, customs clearance, warehousing and global cargo solutions from SAIF Zone, Sharjah, UAE.
          </p>

          <div className="flex flex-col gap-3 max-w-xs mx-auto">
            <a
              href="#contact"
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-blue-700 to-blue-600 text-white font-bold text-xs shadow-md"
            >
              <i className="fas fa-paper-plane" />
              <span>Request a Free Quote</span>
            </a>
            <a
              href="#services"
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 font-bold text-xs shadow-sm"
            >
              <i className="fas fa-list text-blue-700" />
              <span>Our Services</span>
            </a>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-slate-200/60 max-w-xs mx-auto">
            {[
              ["15+", "Years"],
              ["500+", "Clients"],
              ["100K+", "Cargo"],
            ].map(([n, l]) => (
              <div key={l} className="text-center">
                <div className="text-lg font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                  {n}
                </div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="px-4 py-12" id="about">
        <div className="text-center space-y-3">
          <span className="inline-flex items-center px-3.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-wider text-[11px]">
            About Us
          </span>
          <h2 className="text-2xl font-black text-slate-900 leading-tight">
            Professional Freight Forwarding & Customs Clearance
          </h2>
          <p className="text-xs text-slate-600 leading-relaxed font-medium">
            BAJABAR INCORPORATION FZE is a trusted logistics and freight forwarding company based in SAIF Zone, Sharjah, UAE.
          </p>
        </div>

        {/* Highlight Bullets */}
        <div className="grid grid-cols-2 gap-2.5 mt-6">
          {[
            "Customs Clearance",
            "Global Freight Network",
            "Fast Cargo Handling",
            "15+ Years Experience",
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-2 bg-white border border-blue-100 rounded-xl p-2.5 shadow-sm"
            >
              <div className="w-6 h-6 rounded-lg bg-blue-100 text-blue-700 grid place-items-center text-xs shrink-0">
                <i className="fas fa-check" />
              </div>
              <div className="font-semibold text-slate-700 text-[11px] leading-tight">
                {item}
              </div>
            </div>
          ))}
        </div>

        {/* Video Embed (Mobile View) */}
<div className="mt-6 w-full rounded-2xl bg-[#2E5FA8] overflow-hidden p-1 shadow-lg border border-blue-100">
  <video 
    controls 
    playsInline
    className="w-full aspect-video object-contain rounded-xl bg-black/20 block"
    onTimeUpdate={(e) => {
      // Once video hits 44 seconds, restart from the beginning
      if (e.target.currentTime >= 44) {
        e.target.currentTime = 0;
        e.target.play();
      }
    }}
  >
    <source src="/company-video.mp4" type="video/mp4" />
    Your browser does not support the video tag.
  </video>
</div>

        {/* TEAM SECTION */}
        <MobileTeamSection />
      </section>

      {/* SERVICES SECTION */}
      <section className="bg-blue-50/50 py-12 px-4" id="services">
        <div className="text-center space-y-2 mb-6">
          <span className="inline-flex items-center px-3.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-wider text-[11px]">
            Our Services
          </span>
          <h2 className="text-2xl font-bold text-blue-950">
            End-to-End Logistics Services
          </h2>
        </div>

        <div className="grid gap-4">
          {services.map((s) => (
            <div
              key={s.id}
              onClick={() => setActiveService(LOGISTICS_SERVICES_DATA[s.id])}
              className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm space-y-3 cursor-pointer active:scale-[0.98] transition-transform duration-150"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-700 text-white grid place-items-center text-base shrink-0 shadow-sm">
                  <i className={`fas ${s.icon}`} />
                </div>
                <h3 className="text-base font-black text-slate-900">{s.title}</h3>
              </div>

              <p className="text-slate-600 text-xs leading-relaxed line-clamp-2">
                {s.desc}
              </p>

              {/* Action Trigger Row */}
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <button 
                  type="button"
                  className="text-xs text-blue-700 font-bold flex items-center gap-1.5"
                >
                  <span>Learn More</span>
                  <i className="fas fa-arrow-right text-[10px]" />
                </button>
                
                <span className="text-[11px] text-slate-400 font-semibold">
                  Tap to open
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* JCTRANS CERTIFICATION */}
      <MobileCertification />

      {/* WHY US */}
      <MobileWhyUs />

      {/* CONTACT FORM */}
      <section className="py-12 px-4" id="contact">
        <MobileContactForm />
      </section>

      {/* SERVICE DETAILS MODAL (BOTTOM SHEET FOR MOBILE) */}
      <AnimatePresence>
        {activeService && (
          <div className="fixed inset-0 z-[110] flex items-end justify-center p-0">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveService(null)}
              className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative w-full bg-white rounded-t-[28px] p-6 shadow-2xl z-10 max-h-[85vh] overflow-y-auto"
            >
              <button 
                onClick={() => setActiveService(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-sm"
              >
                <i className="fas fa-times" />
              </button>

              <div className="flex items-center gap-3 pr-8">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 grid place-items-center text-xl border border-blue-100 shrink-0">
                  <i className={`fas ${activeService.icon}`} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-950">{activeService.title}</h3>
                  <p className="text-[10px] text-slate-400 font-semibold uppercase">Bajabar Logistics</p>
                </div>
              </div>

              <p className="text-slate-600 text-xs leading-relaxed mt-4 font-medium">
                {activeService.description}
              </p>

              <div className="mt-5 space-y-2.5">
                <h4 className="font-bold text-slate-950 text-xs">Key Operational Capabilities</h4>
                <div className="grid gap-2">
                  {activeService.details.map((detail, idx) => (
                    <div key={idx} className="flex gap-2.5 items-start p-2.5 rounded-xl bg-slate-50 border border-slate-100/60">
                      <div className="w-5 h-5 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] shrink-0 mt-0.5">
                        <i className="fas fa-check" />
                      </div>
                      <p className="text-slate-600 text-xs font-medium leading-normal">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <a 
                  href="#contact"
                  onClick={() => setActiveService(null)}
                  className="w-full text-center py-3 rounded-full bg-blue-700 text-white font-bold text-xs shadow-md"
                >
                  Consult Expert
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* UTILITY ACTIONS & FOOTER */}
      <FloatingActions />
      <SocialRow />
      <Footer />
    </div>
  );
}